import React, { useEffect, useRef, useState } from "react";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, Ban, CreditCard, Link2, Loader2, PlayCircle, Receipt } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@cms/ui/components/card";
import { Badge } from "@cms/ui/components/badge";
import { Button } from "@cms/ui/components/button";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@cms/ui/components/table";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@cms/ui/components/alert-dialog";
import { useApiQuery } from "@cms/portal-lib/hooks";
import { formatPence, formatTimestamp } from "@cms/portal-lib/format";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@cms/ui/components/select";
import {
  billingStatusBadgeVariant,
  billingStatusLabel,
  cadenceLabel,
  cadenceSuffix,
  invoiceStatusBadgeVariant,
  openInvoiceHtml,
} from "@cms/portal-lib/billing";
import {
  adminInvoiceHtmlPath,
  cancelTenantSubscription,
  completeMandate,
  createAdjustment,
  createMandateLink,
  fetchClientForecast,
  fetchTenantBilling,
  setClientCadence,
  startSubscription,
} from "../lib/billing";
import type { BillingCadence } from "@cms/types";
import { BillingTermCard } from "../components/billing-term-card";
import { BillingAdjustmentsCard } from "../components/billing-adjustments-card";
import { BillingForecastCard } from "../components/billing-forecast-card";
import type { AdminOutletContext } from "../lib/outlet-context";

/**
 * Per-client billing: a setup panel that follows billing_status (mandate link →
 * start subscription → live subscription card with cancel), the current
 * monthly breakdown, one-off adjustments, and the invoice history.
 */
export function ClientBillingPage() {
  const { clientId = "" } = useParams();
  const { api } = useOutletContext<AdminOutletContext>();
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useApiQuery(
    () => (api && clientId ? fetchTenantBilling(api, clientId) : null),
    [api, clientId]
  );
  const forecastQuery = useApiQuery(
    () => (api && clientId ? fetchClientForecast(api, clientId) : null),
    [api, clientId]
  );
  const data = query.data;
  const forecast = forecastQuery.data;

  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");
  const [mandateLink, setMandateLink] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustDescription, setAdjustDescription] = useState("");

  const run = async (label: string, action: () => Promise<unknown>) => {
    if (working) return;
    setWorking(true);
    setActionError("");
    setNotice("");
    try {
      await action();
      if (label) setNotice(label);
      query.reload();
      forecastQuery.reload();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  // GoCardless bounces the customer back with ?redirect_flow_id=… appended to
  // the success URL — complete the mandate exactly once, then clean the URL.
  const completedFlow = useRef("");
  useEffect(() => {
    const flowId = searchParams.get("redirect_flow_id");
    if (!api || !clientId || !flowId || completedFlow.current === flowId) return;
    completedFlow.current = flowId;
    run("Direct Debit mandate is set up.", () => completeMandate(api, clientId, flowId))
      .then(() => setSearchParams({}, { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, clientId, searchParams]);

  const tenant = data?.tenant;
  const hasMandate = Boolean(tenant?.gc_mandate_id);
  const hasSubscription = Boolean(tenant?.gc_subscription_id);
  const cadence = tenant?.billing_cadence ?? "monthly";
  const suffix = cadenceSuffix(cadence);

  return (
    <div className="space-y-6">
      {query.error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Couldn't load billing</AlertTitle>
          <AlertDescription>{query.error}</AlertDescription>
        </Alert>
      )}
      {actionError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}
      {notice && (
        <Alert>
          <Receipt className="h-4 w-4" />
          <AlertTitle>Done</AlertTitle>
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      {!data && !query.error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && tenant && (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base">Subscription</CardTitle>
                <Badge variant={billingStatusBadgeVariant(tenant.billing_status)}>
                  {billingStatusLabel(tenant.billing_status)}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                {!hasMandate && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      No Direct Debit mandate yet. Create a setup link and send it to the
                      client — completing it authorises GoCardless to collect payments.
                    </p>
                    <Button
                      size="sm"
                      disabled={working || !api}
                      onClick={() =>
                        run("", async () => {
                          const flow = await createMandateLink(api!, clientId, window.location.href.split("?")[0]);
                          setMandateLink(flow.redirect_url);
                        })
                      }
                    >
                      <Link2 className="h-4 w-4" />
                      Create mandate setup link
                    </Button>
                    {mandateLink && (
                      <div className="space-y-1.5">
                        <Label htmlFor="mandate-link">Send this link to the client</Label>
                        <div className="flex gap-2">
                          <Input id="mandate-link" readOnly value={mandateLink} onFocus={(e) => e.currentTarget.select()} />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigator.clipboard?.writeText(mandateLink)}
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {!hasSubscription && (
                  <div className="space-y-1.5">
                    <Label htmlFor="billing-cadence">Billing cadence</Label>
                    <Select
                      value={cadence}
                      onValueChange={(value: string) =>
                        run(`Billing cadence set to ${cadenceLabel(value as BillingCadence).toLowerCase()}.`, () =>
                          setClientCadence(api!, clientId, value as BillingCadence)
                        )
                      }
                    >
                      <SelectTrigger id="billing-cadence" className="w-40" disabled={working || !api}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Annual bills the plan at the annual rate (two months free) and modules
                      at 12&times; monthly, collected once a year.
                    </p>
                  </div>
                )}

                {hasMandate && !hasSubscription && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Mandate ready ({tenant.gc_mandate_id}). Start the {cadenceLabel(cadence).toLowerCase()} subscription
                      {data.charge ? ` at ${formatPence(data.charge.total_pence)}${suffix}` : ""}.
                    </p>
                    <Button size="sm" disabled={working || !api} onClick={() => run("Subscription started.", () => startSubscription(api!, clientId))}>
                      <PlayCircle className="h-4 w-4" />
                      Start subscription
                    </Button>
                  </>
                )}

                {hasSubscription && (
                  <>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <dt className="text-muted-foreground">Amount</dt>
                      <dd className="font-medium">
                        {data.subscription?.amount_pence != null
                          ? `${formatPence(data.subscription.amount_pence)}${suffix}`
                          : data.charge
                            ? `${formatPence(data.charge.total_pence)}${suffix} (computed)`
                            : "—"}
                      </dd>
                      <dt className="text-muted-foreground">Billing</dt>
                      <dd>
                        {cadenceLabel(cadence)}
                        <span className="text-xs text-muted-foreground"> — cancel the subscription to change</span>
                      </dd>
                      <dt className="text-muted-foreground">Next charge</dt>
                      <dd>{data.subscription?.next_charge_date ?? "—"}</dd>
                      <dt className="text-muted-foreground">Subscription</dt>
                      <dd className="font-mono text-xs">{tenant.gc_subscription_id}</dd>
                      <dt className="text-muted-foreground">Mandate</dt>
                      <dd className="font-mono text-xs">{tenant.gc_mandate_id}</dd>
                    </dl>
                    <Button size="sm" variant="outline" disabled={working} onClick={() => setConfirmCancel(true)}>
                      <Ban className="h-4 w-4" />
                      Cancel subscription
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{cadenceLabel(cadence)} charge</CardTitle>
                <CardDescription>
                  Plan and active modules — plan changes (Overview) and module toggles
                  (Modules) reprice the subscription from the next charge
                  {cadence === "annual" ? " (the next annual renewal — bridge mid-year additions with a one-off adjustment)" : ""}.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data.charge ? (
                  <dl className="space-y-1 text-sm">
                    {data.charge.lines.map((line) => (
                      <div key={`${line.kind}-${line.slug ?? line.description}`} className="flex justify-between">
                        <dt className="text-muted-foreground">{line.description}</dt>
                        <dd>{formatPence(line.amount_pence)}</dd>
                      </div>
                    ))}
                    {data.charge.vat_pence > 0 && (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">VAT ({(data.charge.vat_rate_bps / 100).toFixed(0)}%)</dt>
                        <dd>{formatPence(data.charge.vat_pence)}</dd>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-1 font-semibold">
                      <dt>Total</dt>
                      <dd>{formatPence(data.charge.total_pence)}{suffix}</dd>
                    </div>
                  </dl>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No price configured for plan '{tenant.plan}'.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">One-off adjustment</CardTitle>
              <CardDescription>
                Charges the client's mandate immediately (setup fee, extra work) and lands
                on the ledger as its own invoice. Credits are refunds — issue those from the
                GoCardless dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="adj-amount">Amount (£)</Label>
                  <Input
                    id="adj-amount"
                    className="w-28"
                    inputMode="decimal"
                    placeholder="50.00"
                    value={adjustAmount}
                    onChange={(e) => setAdjustAmount(e.target.value)}
                  />
                </div>
                <div className="min-w-64 flex-1 space-y-1.5">
                  <Label htmlFor="adj-desc">Description (appears on the invoice)</Label>
                  <Input
                    id="adj-desc"
                    placeholder="Website setup fee"
                    value={adjustDescription}
                    onChange={(e) => setAdjustDescription(e.target.value)}
                  />
                </div>
                <Button
                  size="sm"
                  disabled={working || !api || !hasMandate || !adjustDescription.trim() || !(parseFloat(adjustAmount) > 0)}
                  onClick={() =>
                    run("Adjustment charged — the invoice appears once GoCardless confirms.", async () => {
                      await createAdjustment(api!, clientId, Math.round(parseFloat(adjustAmount) * 100), adjustDescription.trim());
                      setAdjustAmount("");
                      setAdjustDescription("");
                    })
                  }
                >
                  <CreditCard className="h-4 w-4" />
                  Charge
                </Button>
              </div>
              {!hasMandate && (
                <p className="mt-2 text-xs text-muted-foreground">Needs an active mandate first.</p>
              )}
            </CardContent>
          </Card>

          <BillingTermCard
            api={api}
            clientId={clientId}
            term={forecast?.term ?? null}
            onChanged={(label) => {
              setActionError("");
              setNotice(label);
              query.reload();
              forecastQuery.reload();
            }}
            onError={setActionError}
          />

          {forecast && (
            <BillingAdjustmentsCard
              api={api}
              clientId={clientId}
              adjustments={forecast.adjustments}
              lines={forecast.months[0]?.lines ?? []}
              onChanged={(label) => {
                setActionError("");
                setNotice(label);
                query.reload();
                forecastQuery.reload();
              }}
              onError={setActionError}
            />
          )}

          {forecast && <BillingForecastCard forecast={forecast} />}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Invoices</CardTitle>
            </CardHeader>
            <CardContent>
              {data.invoices.length === 0 ? (
                <p className="py-6 text-sm text-muted-foreground">No invoices yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Issued</TableHead>
                      <TableHead>Paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <button
                            type="button"
                            className="font-medium text-primary hover:underline"
                            onClick={() => {
                              if (api) {
                                openInvoiceHtml(api, adminInvoiceHtmlPath(clientId, invoice.id))
                                  .catch((err: Error) => setActionError(err.message));
                              }
                            }}
                          >
                            {invoice.number}
                          </button>
                        </TableCell>
                        <TableCell>{formatPence(invoice.total_pence)}</TableCell>
                        <TableCell>
                          <Badge variant={invoiceStatusBadgeVariant(invoice.status)}>{invoice.status}</Badge>
                        </TableCell>
                        <TableCell>{formatTimestamp(invoice.issued_at)}</TableCell>
                        <TableCell>{formatTimestamp(invoice.paid_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <AlertDialog open={confirmCancel} onOpenChange={(open: boolean) => !working && setConfirmCancel(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              GoCardless stops collecting immediately and any scheduled payments are
              cancelled. The client keeps portal access but publishing is disabled until
              a new subscription starts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={working}>Keep subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                run("Subscription cancelled.", () => cancelTenantSubscription(api!, clientId))
                  .then(() => setConfirmCancel(false));
              }}
              disabled={working || !api}
            >
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
